<template>
  <div class="examination-situation-container">
    <headers />

    <div class="content">
      <!-- 概览与答题卡顶部区域 -->
      <div class="overview-section">
        <div class="overview-header flex-between">
          <div class="overview-title">答题结果概览</div>
          <div class="legend-box">
            <div class="legend-item">
              <span class="dot dot-gray"></span>
              <span>未答</span>
            </div>
            <div class="legend-item">
              <span class="dot dot-green"></span>
              <span>答对</span>
            </div>
            <div class="legend-item">
              <span class="dot dot-red"></span>
              <span>答错</span>
            </div>
          </div>
        </div>

        <!-- 统计数字展示卡片 -->
        <div class="stat-cards flex-between">
          <div class="stat-card">
            <div class="stat-num color-total">{{ infoData.totalNum || 0 }}<span class="unit">题</span></div>
            <div class="stat-label">总题数</div>
          </div>
          <div class="stat-card">
            <div class="stat-num color-right">{{ infoData.rightNum || 0 }}<span class="unit">题</span></div>
            <div class="stat-label">答对题数</div>
          </div>
          <div class="stat-card">
            <div class="stat-num color-wrong">{{ wrongNum }}<span class="unit">题</span></div>
            <div class="stat-label">答错题数</div>
          </div>
          <div class="stat-card">
            <div class="stat-num color-rate">{{ accuracyRate }}<span class="unit">%</span></div>
            <div class="stat-label">正确率</div>
          </div>
        </div>

        <!-- 快捷答题卡 -->
        <div class="card-grid-title">快捷答题卡（点击可定位至题目）</div>
        <div class="resultList">
          <div
            v-for="(item, index) in processedResultList"
            :key="item.id || index"
            :class="[
              'resultListItem',
              item.state == 1 && 'resultListItem1',
              item.state == 2 && 'resultListItem2',
              item.state == 3 && 'resultListItem3',
            ]"
            @click="scrollToQuestion(index)"
          >
            {{ index + 1 }}
          </div>
        </div>
      </div>

      <!-- 筛选与答题对比区域 -->
      <div class="detail-section">
        <div class="filter-header flex-between">
          <div class="filter-tabs">
            <div
              class="tab-item"
              :class="{ active: activeTab === 'all' }"
              @click="handleTabChange('all')"
            >
              全部题目 ({{ processedResultList.length }})
            </div>
            <div
              class="tab-item wrong-tab"
              :class="{ active: activeTab === 'wrong' }"
              @click="handleTabChange('wrong')"
            >
              仅看错题 ({{ wrongNum }})
            </div>
            <div
              class="tab-item right-tab"
              :class="{ active: activeTab === 'right' }"
              @click="handleTabChange('right')"
            >
              仅看答对 ({{ infoData.rightNum || 0 }})
            </div>
          </div>
        </div>

        <!-- 题目详情与错题对比列表 -->
        <div class="question-list" v-if="filteredList.length">
          <div
            v-for="item in filteredList"
            :key="item.id || item.originalIndex"
            :id="'question-item-' + item.originalIndex"
            class="question-card"
            :class="[
              item.state == 1 ? 'card-border-green' : (item.state == 2 ? 'card-border-red' : 'card-border-gray')
            ]"
          >
            <!-- 题目头部栏 -->
            <div class="question-header flex-between">
              <div class="header-left">
                <span class="q-number">第 {{ item.originalIndex + 1 }} 题</span>
                <span class="q-type-tag">{{ getTypeName(item.type) }}</span>
              </div>
              <div class="header-right">
                <span v-if="item.state == 1" class="status-tag status-right">
                  <i class="el-icon-check"></i> 答对
                </span>
                <span v-else-if="item.state == 2" class="status-tag status-wrong">
                  <i class="el-icon-close"></i> 答错
                </span>
                <span v-else class="status-tag status-unanswered">
                  <i class="el-icon-minus"></i> 未答
                </span>
              </div>
            </div>

            <!-- 题目标题 -->
            <div class="question-title">
              {{ item.title }}
            </div>

            <!-- 选项列表 -->
            <div class="options-box">
              <div
                v-for="opt in item.optionList"
                :key="opt.key"
                class="option-item"
                :class="opt.statusClass"
              >
                <div class="option-icon-box">
                  <i v-if="opt.isUserSelected && opt.isRealAnswer" class="el-icon-check icon-green"></i>
                  <i v-else-if="opt.isUserSelected && !opt.isRealAnswer" class="el-icon-close icon-red"></i>
                  <i v-else-if="!opt.isUserSelected && opt.isRealAnswer" class="el-icon-check icon-miss"></i>
                  <span v-else class="icon-key">{{ opt.key }}</span>
                </div>
                <div class="option-content">
                  <span class="opt-key">{{ opt.key }}.</span>
                  <span class="opt-text">{{ opt.content }}</span>
                </div>
                <div class="option-tags">
                  <span v-if="opt.isUserSelected && opt.isRealAnswer" class="opt-tag tag-user-right">您的选择（正确）</span>
                  <span v-else-if="opt.isUserSelected && !opt.isRealAnswer" class="opt-tag tag-user-wrong">您的选择（错误）</span>
                  <span v-else-if="!opt.isUserSelected && opt.isRealAnswer" class="opt-tag tag-real-miss">正确答案</span>
                </div>
              </div>
            </div>

            <!-- 答案对比总结卡片 -->
            <div class="comparison-bar" :class="item.state == 1 ? 'bg-right-bar' : (item.state == 2 ? 'bg-wrong-bar' : 'bg-gray-bar')">
              <div class="comparison-item">
                <span class="comp-label">您的选择：</span>
                <span :class="['comp-value', item.state == 1 ? 'val-green' : (item.state == 2 ? 'val-red' : 'val-gray')]">
                  {{ item.userAnswerStr }}
                </span>
              </div>
              <div class="comparison-divider"></div>
              <div class="comparison-item">
                <span class="comp-label">正确答案：</span>
                <span class="comp-value val-green-bold">
                  {{ item.realAnswerStr }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 空数据提示 -->
        <div v-else class="empty-box">
          <el-empty description="暂无对应题目"></el-empty>
        </div>

        <!-- 底部返回按钮 -->
        <div class="footer-actions flexCenter">
          <div class="contentBtn flexCenter" @click="goBack">返回</div>
        </div>
      </div>
    </div>

    <openCode ref="openCode" />
    <footers />
  </div>
</template>

<script>
import headers from "@/components/header.vue";
import footers from "@/components/footer.vue";
import search from "@/components/search.vue";
import openCode from "@/components/openCode.vue";
import { examSituation } from "@/api/examination";

export default {
  name: "examinationSituation",
  components: {
    headers,
    footers,
    search,
    openCode,
  },
  data() {
    return {
      result: {},
      resultList: [], // 接口返回的原答题列表
      situationId: "", // 查看答题情况id
      infoData: {}, // 概览接口数据
      activeTab: "all", // 题目筛选Tab：all-全部，wrong-仅看错题，right-仅看答对
    };
  },
  computed: {
    /**
     * 计算答错的题目数量
     * @returns {number} 错题数量
     */
    wrongNum() {
      const total = Number(this.infoData.totalNum) || 0;
      const right = Number(this.infoData.rightNum) || 0;
      return Math.max(0, total - right);
    },

    /**
     * 计算答题正确率百分比
     * @returns {number} 正确率 (0 - 100)
     */
    accuracyRate() {
      const total = Number(this.infoData.totalNum) || 0;
      const right = Number(this.infoData.rightNum) || 0;
      if (!total) return 0;
      return Math.round((right / total) * 100);
    },

    /**
     * 对原始答题结果进行格式化与预处理，增强每个题目卡片及选项数据
     * @returns {Array<Object>} 处理后的题目列表数据
     */
    processedResultList() {
      if (!this.resultList || !Array.isArray(this.resultList)) return [];

      return this.resultList.map((item, index) => {
        // 解析学员选择的答案数组与正确答案数组
        const userAnswers = this.parseAnswerStr(item.answer);
        const realAnswers = this.parseAnswerStr(item.realAnswer || item.rightAnswer);

        // 格式化展示文本
        const userAnswerStr = userAnswers.length > 0 ? userAnswers.join("、") : "未作答";
        const realAnswerStr = realAnswers.length > 0 ? realAnswers.join("、") : "无";

        // 解析选项 json
        const optionsJson = item.titleOptionJson || {};
        const optionList = Object.entries(optionsJson).map(([key, content]) => {
          const isUserSelected = userAnswers.includes(key);
          const isRealAnswer = realAnswers.includes(key);

          // 决定选项整行的背景样式 Class
          let statusClass = "is-normal";
          if (isUserSelected && isRealAnswer) {
            statusClass = "is-user-right";
          } else if (isUserSelected && !isRealAnswer) {
            statusClass = "is-user-wrong";
          } else if (!isUserSelected && isRealAnswer) {
            statusClass = "is-real-miss";
          }

          return {
            key,
            content,
            isUserSelected,
            isRealAnswer,
            statusClass,
          };
        });

        return {
          ...item,
          originalIndex: index,
          userAnswers,
          realAnswers,
          userAnswerStr,
          realAnswerStr,
          optionList,
        };
      });
    },

    /**
     * 根据当前选中的 Tab 标签，过滤出需要显示的题目列表
     * @returns {Array<Object>} 过滤后的题目数组
     */
    filteredList() {
      const list = this.processedResultList;
      if (this.activeTab === "wrong") {
        // 过滤答错 (state == 2) 或者未答 (state == 3) 的题目
        return list.filter((item) => item.state == 2 || item.state == 3);
      } else if (this.activeTab === "right") {
        // 过滤答对 (state == 1) 的题目
        return list.filter((item) => item.state == 1);
      }
      return list;
    },
  },

  created() {
    this.result = this.$route.query;
    this.situationId = this.$route.query.id || "";
  },

  mounted() {
    this.getSituation();
  },

  methods: {
    /**
     * 解析答案字符串为大写字母数组
     * 支持输入 "A,B"、"A, B"、"AB"、"A" 等格式
     * @param {string|Array} str - 待解析的答案字符串或数组
     * @returns {Array<string>} 标准化的选项 key 数组，如 ['A', 'B']
     */
    parseAnswerStr(str) {
      if (!str) return [];
      if (Array.isArray(str)) {
        return str.map((s) => String(s).trim().toUpperCase()).filter(Boolean);
      }
      return String(str)
        .replace(/[,，\s]/g, "")
        .toUpperCase()
        .split("")
        .filter(Boolean);
    },

    /**
     * 根据题型 ID 返回直观的题型中文名称
     * @param {number|string} type - 题目类型 (1-单选, 2-多选, 3-判断)
     * @returns {string} 题型名称
     */
    getTypeName(type) {
      const typeMap = {
        1: "单选题",
        2: "多选题",
        3: "判断题",
      };
      return typeMap[type] || "单选题";
    },

    /**
     * 发起 API 请求，获取答题情况详情数据
     * @returns {void}
     */
    getSituation() {
      if (!this.situationId) return;
      examSituation({
        id: this.situationId,
      }).then((res) => {
        if (res && res.code == 200) {
          this.resultList = res.data.results || [];
          this.infoData = res.data || {};
        }
      });
    },

    /**
     * 切换题目筛选 Tab 标签
     * @param {string} tabKey - 目标 Tab 的 key 标识 ('all' | 'wrong' | 'right')
     * @returns {void}
     */
    handleTabChange(tabKey) {
      this.activeTab = tabKey;
    },

    /**
     * 点击快捷答题卡，平滑滚动定位至指定题目的卡片位置
     * 如果该题目在当前 Tab 被过滤了，自动切换到“全部题目”以展现目标题
     * @param {number} originalIndex - 目标题目在全量列表中的原始索引
     * @returns {void}
     */
    scrollToQuestion(originalIndex) {
      const existsInCurrentTab = this.filteredList.some(
        (item) => item.originalIndex === originalIndex
      );
      if (!existsInCurrentTab) {
        this.activeTab = "all";
      }

      this.$nextTick(() => {
        const targetEl = document.getElementById(
          `question-item-${originalIndex}`
        );
        if (targetEl) {
          targetEl.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });
    },

    /**
     * 返回上一级页面
     * 优先返回历史上一页（如课程任务/商学院页面等）；若无历史记录则安全跳转至商学院首页
     * @returns {void}
     */
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push("/school/index");
      }
    },
  },
};
</script>

<style lang="less" scoped>
.examination-situation-container {
  background-color: #f8fafc;
  min-height: 100vh;
}

.flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.flexCenter {
  display: flex;
  align-items: center;
  justify-content: center;
}

.content {
  width: 1200px;
  margin: 30px auto 60px;
  box-sizing: border-box;
  text-align: left;
}

/* 顶部概览区域 */
.overview-section {
  background: #ffffff;
  border-radius: 12px;
  padding: 28px 32px;
  box-shadow: 0 4px 20px rgba(0, 22, 26, 0.04);
  margin-bottom: 24px;

  .overview-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f1f5f9;

    .overview-title {
      font-size: 20px;
      font-weight: 600;
      color: #0f172a;
    }

    .legend-box {
      display: flex;
      align-items: center;
      gap: 20px;
      font-size: 14px;
      color: #64748b;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
      }
      .dot-gray { background: #cbd5e1; }
      .dot-green { background: #00d8c3; }
      .dot-red { background: #fa746b; }
    }
  }

  .stat-cards {
    gap: 16px;
    margin-bottom: 28px;

    .stat-card {
      flex: 1;
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 20px;
      text-align: center;
      transition: all 0.25s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.05);
      }

      .stat-num {
        font-size: 32px;
        font-weight: 700;
        line-height: 1.2;
        margin-bottom: 6px;

        .unit {
          font-size: 14px;
          font-weight: 400;
          margin-left: 4px;
        }
      }
      .color-total { color: #3b82f6; }
      .color-right { color: #00d8c3; }
      .color-wrong { color: #fa746b; }
      .color-rate { color: #8b5cf6; }

      .stat-label {
        font-size: 14px;
        color: #64748b;
      }
    }
  }

  .card-grid-title {
    font-size: 15px;
    font-weight: 500;
    color: #475569;
    margin-bottom: 14px;
  }

  .resultList {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;

    .resultListItem {
      width: 38px;
      height: 38px;
      background: #f1f5f9;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: 500;
      color: #334155;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.1);
      }
    }
    .resultListItem1 {
      color: #ffffff;
      background: #00d8c3;
    }
    .resultListItem2 {
      color: #ffffff;
      background: #fa746b;
    }
    .resultListItem3 {
      color: #64748b;
      background: #e2e8f0;
    }
  }
}

/* 详情与答题对比区域 */
.detail-section {
  .filter-header {
    margin-bottom: 20px;

    .filter-tabs {
      display: flex;
      background: #ffffff;
      padding: 6px;
      border-radius: 10px;
      box-shadow: 0 2px 10px rgba(0, 22, 26, 0.03);

      .tab-item {
        padding: 10px 24px;
        font-size: 15px;
        font-weight: 500;
        color: #64748b;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
          color: #0f172a;
        }

        &.active {
          background: #00d8c3;
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(0, 216, 195, 0.3);
        }

        &.wrong-tab.active {
          background: #fa746b;
          box-shadow: 0 2px 8px rgba(250, 116, 107, 0.3);
        }
      }
    }
  }

  .question-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .question-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 24px 28px;
    box-shadow: 0 4px 16px rgba(0, 22, 26, 0.04);
    border-left: 5px solid transparent;
    transition: all 0.2s ease;

    &.card-border-green {
      border-left-color: #00d8c3;
    }
    &.card-border-red {
      border-left-color: #fa746b;
    }
    &.card-border-gray {
      border-left-color: #94a3b8;
    }

    .question-header {
      margin-bottom: 16px;

      .q-number {
        font-size: 16px;
        font-weight: 600;
        color: #0f172a;
        margin-right: 12px;
      }

      .q-type-tag {
        display: inline-block;
        padding: 2px 10px;
        font-size: 12px;
        background: #f1f5f9;
        color: #475569;
        border-radius: 4px;
      }

      .status-tag {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 13px;
        font-weight: 600;
      }
      .status-right {
        background: rgba(0, 216, 195, 0.12);
        color: #00a896;
      }
      .status-wrong {
        background: rgba(250, 116, 107, 0.12);
        color: #d9382e;
      }
      .status-unanswered {
        background: #f1f5f9;
        color: #64748b;
      }
    }

    .question-title {
      font-size: 16px;
      line-height: 1.6;
      color: #1e293b;
      font-weight: 500;
      margin-bottom: 20px;
    }

    /* 选项框样式 */
    .options-box {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 20px;

      .option-item {
        display: flex;
        align-items: center;
        padding: 12px 18px;
        border-radius: 8px;
        border: 1px solid #e2e8f0;
        background: #ffffff;
        transition: all 0.2s ease;

        .option-icon-box {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          font-size: 14px;
          flex-shrink: 0;

          .icon-key {
            font-weight: 600;
            color: #64748b;
          }
          .icon-green {
            color: #00a896;
            font-weight: bold;
            font-size: 16px;
          }
          .icon-red {
            color: #d9382e;
            font-weight: bold;
            font-size: 16px;
          }
          .icon-miss {
            color: #00a896;
            font-weight: bold;
            font-size: 16px;
          }
        }

        .option-content {
          flex: 1;
          font-size: 15px;
          color: #334155;
          line-height: 1.4;

          .opt-key {
            font-weight: 600;
            margin-right: 6px;
          }
        }

        .option-tags {
          margin-left: 12px;

          .opt-tag {
            font-size: 12px;
            padding: 3px 10px;
            border-radius: 4px;
            font-weight: 500;
          }
          .tag-user-right {
            background: #00d8c3;
            color: #ffffff;
          }
          .tag-user-wrong {
            background: #fa746b;
            color: #ffffff;
          }
          .tag-real-miss {
            background: rgba(0, 216, 195, 0.15);
            color: #00a896;
            border: 1px dashed #00d8c3;
          }
        }

        /* 选项状态行背景与边框 */
        &.is-user-right {
          background: rgba(0, 216, 195, 0.08);
          border-color: #00d8c3;
        }

        &.is-user-wrong {
          background: rgba(250, 116, 107, 0.08);
          border-color: #fa746b;
        }

        &.is-real-miss {
          background: rgba(0, 216, 195, 0.03);
          border: 1px dashed #00d8c3;
        }

        &.is-normal {
          background: #ffffff;
        }
      }
    }

    /* 答案对比总结栏 */
    .comparison-bar {
      display: flex;
      align-items: center;
      padding: 14px 20px;
      border-radius: 8px;
      font-size: 15px;

      &.bg-right-bar {
        background: rgba(0, 216, 195, 0.08);
        border: 1px solid rgba(0, 216, 195, 0.2);
      }
      &.bg-wrong-bar {
        background: rgba(250, 116, 107, 0.08);
        border: 1px solid rgba(250, 116, 107, 0.2);
      }
      &.bg-gray-bar {
        background: #f1f5f9;
        border: 1px solid #e2e8f0;
      }

      .comparison-item {
        display: flex;
        align-items: center;

        .comp-label {
          color: #475569;
          font-weight: 500;
        }

        .comp-value {
          font-weight: 600;
        }
        .val-green {
          color: #00a896;
        }
        .val-red {
          color: #d9382e;
        }
        .val-gray {
          color: #64748b;
        }
        .val-green-bold {
          color: #00a896;
          font-size: 16px;
          font-weight: 700;
        }
      }

      .comparison-divider {
        width: 1px;
        height: 16px;
        background: #cbd5e1;
        margin: 0 24px;
      }
    }
  }

  .empty-box {
    background: #ffffff;
    border-radius: 12px;
    padding: 40px;
  }

  .footer-actions {
    margin-top: 40px;

    .contentBtn {
      cursor: pointer;
      width: 280px;
      height: 48px;
      background: #00d8c3;
      border-radius: 6px;
      font-weight: 600;
      font-size: 16px;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba(0, 216, 195, 0.3);
      transition: all 0.2s ease;

      &:hover {
        background: #00c4b1;
        transform: translateY(-1px);
      }
    }
  }
}
</style>